import React, { useEffect, useState } from "react";
import { useLanguage } from "../contexts/LanguageContext";
import { LANGUAGES } from "../i18n/translations";
import { Menu, X } from "lucide-react";

const BrandMark = ({ className = "" }) => (
  <svg
    viewBox="0 0 64 64"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    className={className}
  >
    {/* Rounded square background */}
    <rect x="1" y="1" width="62" height="62" rx="16" fill="#0F2C36" />
    {/* Subtle inner ring */}
    <rect
      x="5"
      y="5"
      width="54"
      height="54"
      rx="13"
      fill="none"
      stroke="#ffffff"
      strokeOpacity="0.12"
      strokeWidth="1"
    />
    {/* Monogram D */}
    <path
      d="M18 16 H30 C40 16 46 22 46 32 C46 42 40 48 30 48 H18 Z M24 22 V42 H29 C35 42 40 38 40 32 C40 26 35 22 29 22 Z"
      fill="#ffffff"
    />
    {/* Accent stroke (F crossbar implied) */}
    <path
      d="M34 30 H48"
      stroke="#6FB3C4"
      strokeWidth="2.5"
      strokeLinecap="round"
    />
    {/* Underline accent */}
    <path
      d="M18 52 H46"
      stroke="#6FB3C4"
      strokeOpacity="0.5"
      strokeWidth="1.2"
      strokeLinecap="round"
    />
  </svg>
);

const Header = () => {
  const { lang, setLang, t } = useLanguage();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const navItems = [
    { id: "services", label: t.nav.services },
    { id: "process", label: t.nav.process },
    { id: "work", label: t.nav.work },
    { id: "about", label: t.nav.about },
    { id: "contact", label: t.nav.contact },
  ];

  const handleNavClick = (e, id) => {
    e.preventDefault();
    setMobileOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "backdrop-blur-xl bg-white/85 border-b border-brand-line/60 py-3"
          : "bg-transparent py-5"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 flex items-center justify-between gap-6">
        {/* Logo wordmark */}
        <a
          href="#top"
          data-testid="header-logo"
          onClick={(e) => handleNavClick(e, "top")}
          className="flex items-center gap-3 group"
          aria-label="DeFalco WebSolution"
        >
          <div className="relative w-10 h-10 rounded-xl overflow-hidden shadow-sm transition-transform duration-300 group-hover:-rotate-3 group-hover:scale-105">
            <BrandMark className="w-full h-full" />
          </div>
          <div className="leading-tight">
            <div className="font-script text-xl sm:text-2xl text-brand -mb-1">
              DeFalco
            </div>
            <div className="text-[9px] sm:text-[10px] tracking-[0.3em] font-semibold text-brand-muted">
              WEB SOLUTION
            </div>
          </div>
        </a>

        {/* Desktop nav */}
        <nav
          className="hidden lg:flex items-center gap-8"
          data-testid="desktop-nav"
        >
          {navItems.map((n) => (
            <a
              key={n.id}
              href={`#${n.id}`}
              onClick={(e) => handleNavClick(e, n.id)}
              data-testid={`nav-${n.id}`}
              className="text-sm font-medium text-brand-muted hover:text-brand transition-colors duration-200 relative group"
            >
              {n.label}
              <span className="absolute -bottom-1 left-0 w-0 h-[1.5px] bg-brand transition-all duration-300 group-hover:w-full" />
            </a>
          ))}
        </nav>

        {/* Right cluster */}
        <div className="flex items-center gap-3 sm:gap-5">
          {/* Language selector */}
          <div
            className="flex items-center gap-1 rounded-full bg-brand-surface/70 border border-brand-line/60 px-1.5 py-1 text-xs sm:text-sm backdrop-blur"
            data-testid="lang-selector"
            role="group"
            aria-label={t.lang.label}
          >
            {LANGUAGES.map((code) => (
              <button
                key={code}
                data-testid={`lang-${code}`}
                onClick={() => setLang(code)}
                className={`px-2.5 py-1 rounded-full font-semibold tracking-wider transition-all duration-200 ${
                  lang === code
                    ? "bg-brand text-white shadow-sm"
                    : "text-brand-muted hover:text-brand"
                }`}
                aria-pressed={lang === code}
              >
                {t.lang[code]}
              </button>
            ))}
          </div>

          {/* CTA button (desktop) */}
          <a
            href="#contact"
            onClick={(e) => handleNavClick(e, "contact")}
            data-testid="header-cta"
            className="hidden md:inline-flex items-center gap-2 rounded-full bg-brand text-white px-5 py-2.5 text-sm font-semibold shadow-sm hover:bg-brand-hover hover:-translate-y-0.5 transition-all duration-300 shine-hover"
          >
            {t.nav.cta}
            <span aria-hidden>→</span>
          </a>

          {/* Mobile menu toggle */}
          <button
            className="lg:hidden p-2 rounded-full hover:bg-brand-surface transition"
            onClick={() => setMobileOpen((s) => !s)}
            data-testid="mobile-menu-toggle"
            aria-label="Menu"
          >
            {mobileOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {/* Mobile nav drawer */}
      {mobileOpen && (
        <div
          className="lg:hidden bg-white border-t border-brand-line/60"
          data-testid="mobile-nav"
        >
          <nav className="flex flex-col px-6 py-4 gap-3">
            {navItems.map((n) => (
              <a
                key={n.id}
                href={`#${n.id}`}
                onClick={(e) => handleNavClick(e, n.id)}
                data-testid={`mobile-nav-${n.id}`}
                className="text-base font-medium text-brand py-2 border-b border-brand-line/40"
              >
                {n.label}
              </a>
            ))}
            <a
              href="#contact"
              onClick={(e) => handleNavClick(e, "contact")}
              className="mt-3 inline-flex items-center justify-center gap-2 rounded-full bg-brand text-white px-5 py-3 text-sm font-semibold"
            >
              {t.nav.cta} →
            </a>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
